<div class="dashboard-container" *ngIf="data">
  <!-- Header -->
  <header class="dashboard-header">
    <div class="header-content">
      <h1>Analytics Dashboard</h1>
      <div class="header-info">
        <span class="data-type">{{ currentDataset?.displayName || 'Data' }}</span>
        <span class="file-name">{{ currentDataset?.filename || data.zipFilename }}</span>
        <span class="upload-date">{{ data.uploadedAt | date:'short' }}</span>
        <span class="debug-info">{{ processedData.length }} rows processed</span>
        <button class="clear-btn" (click)="clearData()">
          Clear Data
        </button>
      </div>
    </div>
  </header>

  <!-- Date Filter Section -->
  <section class="date-filter-section">
    <div class="date-filter-container">
      <div class="time-period-buttons">
        <button 
          class="time-period-btn"
          [class.active]="selectedPreset === '7days'"
          (click)="setDateFilter('7days')">
          Last 7 days
        </button>
        <button 
          class="time-period-btn"
          [class.active]="selectedPreset === '28days'"
          (click)="setDateFilter('28days')">
          Last 28 days
        </button>
        <button 
          class="time-period-btn"
          [class.active]="selectedPreset === '3months'"
          (click)="setDateFilter('3months')">
          Last 3 months
        </button>
        <button 
          class="time-period-btn more-btn"
          [class.active]="showDatePicker"
          (click)="showDatePicker = !showDatePicker">
          More
        </button>
      </div>
      
      <div class="date-range-display">
        {{ getDateFilterDisplayText() }}
        <span class="estimation-indicator" *ngIf="isShowingEstimatedData()">
          📊 Estimated data
        </span>
      </div>
    </div>

    <!-- Date Picker Modal -->
    <div class="date-picker-modal" *ngIf="showDatePicker" (click)="showDatePicker = false">
      <div class="date-picker-content" (click)="$event.stopPropagation()">
        <h3>Date range</h3>
        
        <div class="date-picker-tabs">
          <button 
            class="picker-tab"
            [class.active]="!isCustomDateFilterActive()"
            (click)="setDateFilterToPreset()">
            Filter
          </button>
          <button 
            class="picker-tab"
            [class.active]="isCustomDateFilterActive()"
            (click)="setCustomDateFilter()">
            Custom
          </button>
        </div>

        <div class="date-picker-options" *ngIf="!isCustomDateFilterActive()">
          <div class="preset-option" 
               [class.active]="selectedPreset === '7days'"
               (click)="setDateFilter('7days')">
            <span class="radio-btn"></span>
            Last 7 days
          </div>
          <div class="preset-option" 
               [class.active]="selectedPreset === '28days'"
               (click)="setDateFilter('28days')">
            <span class="radio-btn"></span>
            Last 28 days
          </div>
          <div class="preset-option" 
               [class.active]="selectedPreset === '3months'"
               (click)="setDateFilter('3months')">
            <span class="radio-btn"></span>
            Last 3 months
          </div>
          <div class="preset-option" 
               [class.active]="selectedPreset === '6months'"
               (click)="setDateFilter('6months')">
            <span class="radio-btn"></span>
            Last 6 months
          </div>
          <div class="preset-option" 
               [class.active]="selectedPreset === '12months'"
               (click)="setDateFilter('12months')">
            <span class="radio-btn"></span>
            Last 12 months
          </div>
          <div class="preset-option" 
               [class.active]="selectedPreset === '16months'"
               (click)="setDateFilter('16months')">
            <span class="radio-btn"></span>
            Last 16 months
          </div>
        </div>

        <div class="custom-date-inputs" *ngIf="isCustomDateFilterActive()">
          <div class="date-input-group">
            <label>Start date</label>
            <input 
              type="date" 
              [(ngModel)]="customStartDate"
              placeholder="yyyy-mm-dd">
          </div>
          <div class="date-input-group">
            <label>End date</label>
            <input 
              type="date" 
              [(ngModel)]="customEndDate"
              placeholder="yyyy-mm-dd">
          </div>
        </div>

        <div class="date-picker-actions">
          <button class="cancel-btn" (click)="cancelCustomDateFilter()">Cancel</button>
          <button class="apply-btn" 
                  *ngIf="isCustomDateFilterActive()" 
                  (click)="applyCustomDateFilter()">Apply</button>
        </div>
      </div>
    </div>
  </section>

  <!-- Summary Stats -->
  <section class="summary-section">
    <div class="summary-cards">
      <div class="summary-card clicks">
        <div class="card-content">
          <div class="card-label">Total Clicks</div>
          <div class="card-value">{{ formatNumberCompact(summaryStats.totalClicks) }}</div>
        </div>
      </div>
      
      <div class="summary-card impressions">
        <div class="card-content">
          <div class="card-label">Total Impressions</div>
          <div class="card-value">{{ formatNumberCompact(summaryStats.totalImpressions) }}</div>
        </div>
      </div>
      
      <div class="summary-card ctr">
        <div class="card-content">
          <div class="card-label">Average CTR</div>
          <div class="card-value">{{ formatPercentage(summaryStats.avgCtr) }}</div>
        </div>
      </div>
      
      <div class="summary-card position">
        <div class="card-content">
          <div class="card-label">Average Position</div>
          <div class="card-value">{{ formatPosition(summaryStats.avgPosition) }}</div>
        </div>
      </div>
    </div>
  </section>

  <!-- Chart Section -->
  <section class="chart-section" *ngIf="dateSeriesData.length > 0">
    <h2>Performance Over Time</h2>
    <div class="chart-container">
      <canvas
        baseChart
        [data]="chartData"
        [options]="chartOptions"
        [type]="chartType">
      </canvas>
    </div>
  </section>

  <!-- Data Table Section -->
  <section class="data-section">
    <!-- GSC-Style Tabs -->
    <div class="tabs gsc-tabs">
      <button 
        *ngFor="let dataset of availableDatasets"
        class="tab gsc-tab"
        [class.active]="activeTab === dataset.type"
        (click)="setActiveTab(dataset.type)">
        {{ dataset.displayName }}
      </button>
    </div>

    <!-- Search and Controls -->
    <div class="controls">
      <div class="search-container">
        <input
          type="text"
          placeholder="Search..."
          class="search-input"
          [(ngModel)]="searchTerm"
          (input)="onSearch()"
        />
      </div>
      
      <div class="results-info">
        Showing {{ displayingStart }}-{{ displayingEnd }} of {{ filteredData.length }} results
      </div>
    </div>

    <!-- Data Table -->
    <div class="table-container">
      <table class="data-table">
        <thead>
          <tr>
            <th 
              class="sortable"
              (click)="onSort(getColumnName())">
              {{ getColumnDisplayName() }}
              <span class="sort-icon">{{ getSortIcon(getColumnName()) }}</span>
            </th>
            <th 
              class="sortable numeric"
              (click)="onSort('clicks')">
              Clicks
              <span class="sort-icon">{{ getSortIcon('clicks') }}</span>
            </th>
            <th 
              class="sortable numeric"
              (click)="onSort('impressions')">
              Impressions
              <span class="sort-icon">{{ getSortIcon('impressions') }}</span>
            </th>
            <th 
              class="sortable numeric"
              (click)="onSort('ctr')">
              CTR
              <span class="sort-icon">{{ getSortIcon('ctr') }}</span>
            </th>
            <th 
              class="sortable numeric"
              (click)="onSort('position')">
              Avg. Position
              <span class="sort-icon">{{ getSortIcon('position') }}</span>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let row of paginatedData">
            <td class="primary-cell">
              <div class="cell-content">
                {{ getCellValue(row) }}
              </div>
            </td>
            <td class="numeric">{{ formatNumber(row.clicks) }}</td>
            <td class="numeric">{{ formatNumber(row.impressions) }}</td>
            <td class="numeric">{{ formatPercentage(row.ctr) }}</td>
            <td class="numeric">{{ formatPosition(row.position) }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Pagination -->
    <div class="pagination" *ngIf="totalPages > 1">
      <button 
        class="page-btn"
        [disabled]="currentPage === 1"
        (click)="goToPage(currentPage - 1)">
        Previous
      </button>
      
      <div class="page-numbers">
        <button
          *ngFor="let page of [].constructor(totalPages); let i = index"
          class="page-btn"
          [class.active]="currentPage === i + 1"
          (click)="goToPage(i + 1)">
          {{ i + 1 }}
        </button>
      </div>
      
      <button 
        class="page-btn"
        [disabled]="currentPage === totalPages"
        (click)="goToPage(currentPage + 1)">
        Next
      </button>
    </div>
  </section>
</div>

<!-- No Data State -->
<div class="no-data" *ngIf="!data">
  <div class="no-data-content">
    <h2>No Data Available</h2>
    <p>Upload a Google Search Console ZIP file to see your analytics dashboard.</p>
  </div>
</div> 